/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { GridColumn } from '@progress/kendo-react-grid';
import FormRow from '../../components/formRow';
import { FormFieldGeneric } from '../../components/formField';
import { OrangeButton } from '../../components/buttonsComponent';
import * as promptService from '../../services/prompt';
import GridInline from '../../components/grid';
import { TotalValueCell } from '../../components/grid/totalCell';
import ToggleExpense from '../../components/toggleInvoice';
import { warningNotification, errorNotification } from '../../components/notification';
import LoadScreen from '../../view/load';
import './style.css';

const chipStyles = (status) => {
  const map = {
    loading: { bg: '#fff7e6', bd: '#ffd591', fg: '#ad6800', text: 'enviando…' },
    ok: { bg: '#eef9f1', bd: '#b7eb8f', fg: '#135200', text: 'ok' },
    error: { bg: '#ffecec', bd: '#ffa39e', fg: '#a8071a', text: 'erro' },
  };
  const s = map[status] || map.ok;
  return {
    chip: {
      fontSize: 12,
      padding: '2px 8px',
      borderRadius: 999,
      background: s.bg,
      border: `1px solid ${s.bd}`,
      color: s.fg,
    },
    text: s.text,
  };
};

const Card = ({ title, subtitle, status, children, tone = 'neutral' }) => {
  const tones = {
    question: { bg: '#ffffff', bd: '#f0f0f0' },
    answer: { bg: '#fafcff', bd: '#e9f0ff' },
    neutral: { bg: '#ffffff', bd: '#f0f0f0' },
  };
  const t = tones[tone] || tones.neutral;
  const chip = chipStyles(status);

  return (
    <div
      style={{
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: 12,
        padding: 12,
        display: 'grid',
        gap: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong>{title}</strong>
        {!!status && <span style={chip.chip}>{chip.text}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.6 }}>{subtitle}</span>
      </div>
      <div>{children}</div>
    </div>
  );
};

const Prompt = () => {
  const [prompt, setPrompt] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [history, setHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAccount, setTotalAccount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const listRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isAccount, setIsAccount] = useState(false);
  const hasItems = history.length > 0;
  const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmed = (prompt ?? '').toString().trim();
    if (!trimmed) {
      warningNotification('Digite uma pergunta antes de enviar.');
      return;
    }
    setLoading(true);
    const id = newId();
    let createdAt = new Date();

    setHistory((prev) => [
      ...prev,
      { id, question: trimmed, answer: '', status: 'loading', createdAt },
    ]);
    setPrompt('');
    setIsSubmitting(true);

    try {
      const body = { Prompt: trimmed, ThreadId: threadId, History: false }
      const response = await promptService.openAI(body);
      createdAt = new Date();
      setTransactions([]);
      calculateTotal([]);
      setAccounts([]);
      calculateAccountTotal([]);
      if (response?.status === 200) {
        const { data } = response;
        setThreadId(data.ThreadId);
        let answer = data.Answer;
        if (data.Status == 'F') {
          answer = JSON.parse(data.Answer);
          setAnswer(answer);
          if (answer.data) {
            answer = JSON.parse(answer.dados);
            answer = answer.map((item) => ({
              ...item,
              Data: new Date(item.Data)
            }));
            setAccounts(answer);
            calculateAccountTotal(answer);
            setIsAccount(true);
          } else {
            answer = JSON.parse(answer.dados);
            answer = answer.map((item) => ({
              ...item,
              Data: new Date(item.Data)
            }));
            setTransactions(answer);
            calculateTotal(answer);
            setIsAccount(false);
          }
          setThreadId(null);
          answer = "Grid gerado!";
        }
        setHistory((prev) =>
          prev.map((item) => (item.id === id ? { ...item, answer: answer, status: 'ok', end: createdAt.toLocaleString() } : item))
        );
      } else {
        const { data } = response || {};
        if (data?.MensagemUsuario !== undefined) warningNotification(data.MensagemUsuario);
        else errorNotification('Houve um erro ao tentar consultar a IA.');

        setHistory((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, answer: data?.MensagemUsuario || '—', status: 'error', end: createdAt.toLocaleString() }
              : item
          )
        );
      }
    } catch (err) {
      console.error(err);
      errorNotification('Falha de comunicação com o serviço de IA.');
      setHistory((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, answer: 'Erro ao consultar a IA.', status: 'error', end: createdAt.toLocaleString() } : item
        )
      );
    } finally {
      setIsSubmitting(false);
    }
    setLoading(false);
  };

  const handleClear = () => {
    if (!hasItems) return;
    setHistory([]);
  };

  const formattedItems = useMemo(
    () =>
      history.map((h) => ({
        ...h,
        when: new Date(h.createdAt).toLocaleString(),
      })),
    [history]
  );

  const calculateTotal = (newTransactions) => {
    const _transactions = !newTransactions ? transactions : newTransactions;
    let total = _transactions.reduce((acc, current) => acc + current.Valor, 0);
    total = Math.round(total * 100) / 100;
    setTotal(total);
    return total;
  };

  const calculateAccountTotal = (newAccounts) => {
    const _accounts = !newAccounts ? accounts : newAccounts;
    let total = _accounts.reduce((acc, current) => acc + current.Saldo, 0);
    total = Math.round(total * 100) / 100;
    setTotalAccount(total);
    return total;
  };

  return (
    <div id="prompt">
      <h1>Prompt - Inteligência Artificial</h1>
      <FormRow>
        <div style={{ display: 'grid', gap: 16, width: '100%' }}>
          <div
            ref={listRef}
            style={{
              marginTop: 12,
              maxHeight: '22vh',
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 12,
              padding: 12,
              background: '#fafafa',
            }}
          >
            {!hasItems && (
              <div style={{ opacity: 0.7 }}>
                Nenhuma conversa ainda. Faça a sua primeira pergunta.
              </div>
            )}

            {formattedItems.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gap: 10,
                  paddingBottom: 14,
                  marginBottom: 14,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Card
                    title=""
                    subtitle={item.when}
                    tone="question"
                  >
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.question}</p>
                  </Card>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Card title="Resposta" subtitle={item.status === 'loading' ? '' : item.end} status={item.status} tone="answer">
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {item.status === 'loading' ? 'Aguardando resposta…' : item.answer}
                    </pre>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </FormRow>
      <form onSubmit={handleSubmit}>
        <FormRow withShrink>
          <FormFieldGeneric
            classNameWrapper="fill-50-field"
            titleLabel="Digite sua pergunta..."
            valueInput={prompt}
            onChangeValue={(e) => setPrompt(e.value)}
          />

          <div className="side-button" style={{ display: 'flex', gap: 8 }}>
            <OrangeButton type="submit" name="enviar" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando…' : 'Enviar'}
            </OrangeButton>
            <OrangeButton type="button" name="limpar" onClick={handleClear} disabled={!hasItems}>
              Limpar histórico
            </OrangeButton>
          </div>
        </FormRow>
        <ToggleExpense title={isAccount ? "Contas" : "Transações"} startExpanded>
          {loading ? (
            <LoadScreen />
          ) : (
            <>
              {isAccount &&
                <FormRow>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", }}>
                    <span><strong>Data:</strong> {answer?.data}</span> |
                    <span><strong>Conta:</strong> {answer?.conta}</span>
                  </div>
                </FormRow>
              }
              {!isAccount &&
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "10px" }}>
                  <span><strong>Data Inicial:</strong> {answer?.dataInicial}</span> |
                  <span><strong>Data Final:</strong> {answer?.dataFinal}</span> |
                  <span><strong>Conta:</strong> {answer?.conta}</span> |
                  <span><strong>Pessoa:</strong> {answer?.pessoa}</span> |
                  <span><strong>Plano de Contas:</strong> {answer?.planoConta}</span> |
                  <span><strong>Centro de Custos:</strong> {answer?.centroCusto}</span> |
                  <span><strong>Tipo Valor:</strong> {answer?.tipoValor}</span> |
                  <span><strong>Tipo Conta:</strong> {answer?.tipoConta}</span> |
                  <span><strong>Excluir Conta Previsão:</strong> {answer?.excluirPrevisao ? "Sim" : "Não"}</span>
                </div>
              }
              <FormRow>
                {isAccount &&
                  <GridInline data={accounts} setData={setAccounts} style={{ maxHeight: '27vh' }}>
                    <GridColumn field="Data" title="Data" format="{0:dd/MM/yyyy}" editable={false} />
                    <GridColumn field="Nome" title="Conta" editable={false} />
                    <GridColumn field="NomeTipoConta" title="Tipo de Conta" editable={false} />
                    <GridColumn
                      field="Saldo"
                      title="Saldo"
                      editable={false}
                      editor="numeric"
                      format="{0:n2}"
                      footerCell={(props) => TotalValueCell(props, totalAccount)}
                    />
                  </GridInline>
                }
                {!isAccount &&
                  <GridInline data={transactions} setData={setTransactions} style={{ maxHeight: '270vh' }}>
                    <GridColumn field="Data" title="Data" format="{0:dd/MM/yyyy}" editable={false} />
                    <GridColumn field="Descricao" title="Descrição" editable={false} />
                    <GridColumn
                      field="Valor"
                      title="Valor"
                      editable={false}
                      editor="numeric"
                      format="{0:n2}"
                      footerCell={(props) => TotalValueCell(props, total)}
                    />
                    <GridColumn field="NomeConta" title="Conta" editable={false} />
                    <GridColumn field="NomePlanoDeContas" title="Plano de Conta" editable={false} />
                    <GridColumn field="NomeCentroDeCustos" title="Centro de Custo" editable={false} />
                  </GridInline>
                }
              </FormRow>
            </>
          )}
        </ToggleExpense>
      </form>
    </div >
  );
};

export default Prompt;
